import React, { useCallback, useEffect, useMemo } from 'react';

import { CloseOutlined, FileDownloadDoneOutlined } from '@mui/icons-material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useParams } from 'react-router';

import { MAX_TILES_LIMIT } from '@/features/Dashboard/constants/const';
import { TAB_DEFAULT_BASE_NAME } from '@/features/Dashboard/constants/names';
import { useDrawerSteps } from '@/features/Dashboard/drawer/context/DrawerStepsContext';
import { DrawerButton } from '@/features/Dashboard/drawer/DrawerButton';
import { DrawerHeader } from '@/features/Dashboard/drawer/DrawerHeader';
import { useBuildDashboardStructure } from '@/features/Dashboard/hooks/useBuildDashboardStructure';
import { useCurrentDashboardConfig } from '@/features/Dashboard/hooks/useCurrentDashboardConfig';
import { getTabContent } from '@/features/Dashboard/lib/transformers';
import { useDashboardEditActions } from '@/features/Dashboard/pages/Edit/hooks/useDashboardEditActions';
import {
  selectCurrentTabId,
  selectInitialDashboardName
} from '@/features/Dashboard/pages/Edit/model/dashboardEdit.selectors';
import type { MarketApp } from '@/features/Dashboard/types';
import { useUpdateDashboard } from '@/hooks/api/dashboard';
import { useAppSelector } from '@/hooks/redux-hooks';
import useSnackbarNotifications from '@/hooks/useSnackbarNotifications';

const AddAppHeader = ({
  createWidget,
  isValid
}: {
  createWidget: () => MarketApp | undefined;
  isValid: boolean;
}) => {
  const { dashboardId, tabName } = useParams<{ dashboardId?: string; tabName?: string }>();
  const initialDashboardNameFromRedux = useAppSelector(selectInitialDashboardName);
  const initialDashboardName =
    initialDashboardNameFromRedux || (dashboardId ? decodeURIComponent(dashboardId) : '');
  const currentTabId = useAppSelector(selectCurrentTabId);
  const {
    closeDrawer,
    handleAttemptBack,
    isEditMode,
    editingTarget,
    setEditingTarget,
    handleAttemptClose
  } = useDrawerSteps();
  const actions = useDashboardEditActions();
  const allDashboardTabs = useCurrentDashboardConfig();
  const { showErrorSnackbar } = useSnackbarNotifications();

  const currentTabName = tabName || Object.keys(allDashboardTabs || {})[0] || TAB_DEFAULT_BASE_NAME;

  const buildDashboardStructure = useBuildDashboardStructure();

  const { mutate, isSuccess, isPending } = useUpdateDashboard(
    initialDashboardName,
    'New app added!',
    currentTabName
  );

  // Get current tiles count for the active tab (VIEW mode validation)
  const currentTilesCount = useMemo(() => {
    if (!allDashboardTabs) return 0;
    const tabValue = allDashboardTabs[currentTabName];
    if (!tabValue) return 0;
    const tabContent = getTabContent(tabValue);
    const tilesWidget = tabContent.find(app => 'type' in app && app.type === 'tiles');
    return tilesWidget && 'tiles' in tilesWidget ? tilesWidget.tiles.length : 0;
  }, [allDashboardTabs, currentTabName]);

  useEffect(() => {
    if (isSuccess) {
      closeDrawer();
    }
  }, [isSuccess, closeDrawer]);

  const handleSave = useCallback(
    (widget: MarketApp | undefined) => {
      if (!widget) return;
      if (isEditMode && !currentTabId) return;

      // In edit mode of the Dashboard, update existing if editing target is set; otherwise add new to draft
      if (isEditMode) {
        if ('type' in widget && widget.type === 'tiles') {
          actions.updateCurrentTabTiles(widget.tiles);
          setEditingTarget(null);
          closeDrawer();
          return;
        }
        if (editingTarget?.appId && editingTarget?.tabId && 'tabs' in widget) {
          const updatedTab = (widget as any).tabs?.[0];
          if (updatedTab && currentTabId) {
            actions.updateAppTab(
              currentTabId,
              editingTarget.appId,
              editingTarget.tabId,
              updatedTab
            );
            setEditingTarget(null);
            closeDrawer();
            return;
          }
        }

        // Add new widget to current tab
        if ('tabs' in widget && currentTabId) {
          actions.addApp(currentTabId, widget);
        } else if ('type' in widget && widget.type === 'tiles') {
          actions.updateCurrentTabTiles(widget.tiles);
        }
        closeDrawer();
        return;
      }

      // VIEW mode: validate tiles limit before saving to backend
      if ('type' in widget && widget.type === 'tiles') {
        const newTilesCount = widget.tiles.length;
        // Check if adding new tiles would exceed the limit
        // Note: When tiles already exist, we replace them; when they don't, we add new ones
        const hasTilesAlready = currentTilesCount > 0;
        const wouldExceedLimit = hasTilesAlready
          ? newTilesCount > MAX_TILES_LIMIT // Replacing existing tiles
          : currentTilesCount + newTilesCount > MAX_TILES_LIMIT; // Adding new tiles

        if (wouldExceedLimit) {
          showErrorSnackbar(`Maximum ${MAX_TILES_LIMIT} highlighted prices allowed per tab.`);
          return;
        }
      }

      const structurePayload = buildDashboardStructure(widget);
      mutate(structurePayload);
    },
    [
      actions,
      buildDashboardStructure,
      closeDrawer,
      currentTabId,
      currentTilesCount,
      editingTarget,
      isEditMode,
      mutate,
      setEditingTarget,
      showErrorSnackbar
    ]
  );

  return (
    <DrawerHeader
      before={
        <DrawerButton
          disabled={isPending}
          icon={<ArrowBackIcon />}
          title='Go back'
          variant='secondary'
          onClick={handleAttemptBack}
        />
      }
      buttons={
        <>
          <DrawerButton
            disabled={isPending || !isValid}
            icon={<FileDownloadDoneOutlined />}
            title='Save'
            onClick={() => {
              handleSave(createWidget());
            }}
          />
          <DrawerButton
            disabled={isPending}
            icon={<CloseOutlined />}
            title='Discard'
            variant='secondary'
            onClick={() => {
              if (!isPending) {
                handleAttemptClose();
              }
            }}
          />
        </>
      }
      title={isEditMode ? 'Edit app' : 'Add new app'}
    />
  );
};

export default AddAppHeader;
